// =============================================================================
// OBIEG DOKUMENTÓW RECEPTORA — logika panelu (UD-11a, plan 53 §13.6.4 p. 9
// i §13.6.5). Moduł BEZ zależności od Supabase i od Reacta.
//
// Osobny plik z tego samego powodu co `rodzajeZadanReceptora.ts`: bramka
// `tests/unit/apps/kontraktObieguDokumentow.test.ts` zestawia listy panelu
// z listami USŁUGI (`services/receptor-core`) i z CHECK-ami migracji 786, a
// powłoka (aplikacja Vite) nie może zaimportować niczego z `services/*`. Listy
// żyją więc w kopii — i ta bramka sprawia, że kopia nie rozjedzie się po
// cichu. Logika wariantów siedzi tu, a nie w komponencie, żeby dało się ją
// mierzyć bez renderowania ekranu.
//
// Stany kursora, wyjścia ze stanów, ich zdania i zdania powodów harmonogramu
// mieszkają od okna agenta 0.6.0 (plan okna, D8) w SŁOWNIKU WSPÓLNYM
// `@insolitum/types/receptor`, bo te same zdania pokazuje mapa przepływu
// w oknie agenta. Panel wystawia TE SAME obiekty pod dotychczasowymi nazwami
// (`STANY_KURSORA_PANELU`, `ZDANIA_STANOW`, …) — jeden obiekt pod dwiema
// nazwami importu, nie kopia (`tests/unit/apps/rodzajeZadanReceptora.test.ts`
// sprawdza to tożsamością). Zdań słownika nie wpisuje się tu drugi raz.
//
// TRZY RZECZY, KTÓRE TEN PLIK PILNUJE I KTÓRE ŁATWO ZEPSUĆ:
//
//  * PRAWO PODGLĄDU TO NIE PRAWO DECYZJI. Kursor czyta polityka
//    `is_super_admin() OR is_org_writer(...)` — owner, admin, manager, każdy
//    z AKTYWNYM członkostwem (`status = 'active'`, UD-12a). Decydują
//    (włączają, wstrzymują, wznawiają) tylko owner, admin i super-admin
//    (D53-61): `od` ustala numery, które Receptor nada raz i nieodwracalnie.
//    Partner nie ma ani jednego, ani drugiego, więc dla niego odpowiedź bazy to
//    PUSTA lista, a pusta lista z tym samym zdaniem co „obieg nieaktywny”
//    kłamałaby — stąd osobny wariant „brak prawa podglądu”, w którym panel w ogóle
//    nie pyta bazy.
//  * ZDANIE DLA KAŻDEGO STANU I KAŻDEGO POWODU, a nieznany kod nie udaje znanego.
//    Migracja, która kiedyś dopisze stan do CHECK-a, nie może zamienić go w
//    ekranie w „działa”.
//  * KURSOR BEZ ŻYWEGO PROWADZĄCEGO NIE JEST „INNYM AGENTEM”. Funkcja włączenia
//    (786) przenosi kursor odwołanego albo usuniętego agenta na nowego, a
//    odmawia (`jeden_serwer_w_obiegu`) tylko wtedy, gdy prowadzący żyje. Panel,
//    który każdy cudzy kursor nazwałby „tym serwerem nie jest w obiegu”, nie
//    zostawiłby człowiekowi żadnej drogi po odwołaniu agenta.
//
// Ten plik czyta też lista „Organizacja › Obieg dokumentów” (UD-12a): zdania
// stanów, powodów i porażek odczytu mieszkają TU, a słowa stanów dokumentu —
// w kartotece `@insolitum/types` (`obieg.ts`). Dwa ekrany o tym samym kursorze
// i tym samym dokumencie mówią więc tymi samymi słowami.
// =============================================================================

import { STANY_DOKUMENTU, type KodStanuDokumentu } from '@insolitum/types';
import {
  POWODY_HARMONOGRAMU_RECEPTORA,
  STANY_KURSORA_RECEPTORA,
  WYJSCIE_ZE_STANU,
  ZDANIA_POWODOW_HARMONOGRAMU,
  ZDANIA_STANOW,
  ZDANIE_AUTOMATYCZNA_WERYFIKACJA,
  ZDANIE_WYMAGA_PRZEPIECIA,
  type PowodHarmonogramuReceptora,
  type SposobWyjsciaZeStanu,
  type StanKursoraReceptora,
} from '@insolitum/types/receptor';
import { czyLokalizacjaAktywna } from '@insolitum/utils';

import { formatujCzasWzgledny } from '../printing/status';

// ── Role (D53-61) ───────────────────────────────────────────────────────────

/**
 * Role, które DECYDUJĄ o obiegu: włączają, wstrzymują, wznawiają. KOPIA
 * `ROLE_OBIEGU` z `services/receptor-core/src/bramaUzytkownika.ts` — zgodność co do
 * znaku pilnuje `tests/unit/apps/kontraktObieguDokumentow.test.ts`.
 * `super_admin` jest na liście dla zgodności z usługą; w panelu rozstrzyga o nim
 * flaga `users.is_super_admin` (rola w organizacji nigdy tak nie brzmi).
 */
export const ROLE_OBIEGU_PANELU = ['super_admin', 'owner', 'admin'] as const;

/**
 * Role, które WIDZĄ stan obiegu — polityka odczytu kursora `is_org_writer`
 * (owner, admin, manager) plus super-admin. Węższa lista dałaby kierownika bez
 * podglądu, który w bazie ma do niego prawo; szersza — ekran „nieaktywny” dla
 * kogoś, komu baza oddaje pustą listę (partner).
 */
export const ROLE_PODGLADU_OBIEGU = ['super_admin', 'owner', 'admin', 'manager'] as const;

export interface KontekstObiegu {
  /** Rola w BIEŻĄCEJ organizacji (`currentOrganization.role`). */
  rola: string | null | undefined;
  /**
   * Status członkostwa w BIEŻĄCEJ organizacji (`currentOrganization.status`).
   * Pole WYMAGANE, żeby kompilator wskazał każde wywołanie: `is_org_writer`
   * wpuszcza wyłącznie `status = 'active'`, a powłoka potrafi wybrać organizację
   * z członkostwem `pending` (zapas `orgsData[0]` w `AuthProvider`). Rola bez
   * aktywnego członkostwa dałaby ekran, któremu baza oddaje pustą listę.
   */
  statusCzlonkostwa: string | null | undefined;
  /** Flaga `users.is_super_admin` — jedyne źródło prawa do przepięcia. */
  superAdmin: boolean;
}

export interface PrawaObiegu {
  /** Widzi stan obiegu (i tylko wtedy panel pyta bazę o kursor). */
  podglad: boolean;
  /** Włącza, wstrzymuje, wznawia. */
  decyduje: boolean;
  /** Przepina instalację — wyłącznie super-admin Insolitum. */
  przepina: boolean;
}

export function prawaObiegu(ctx: KontekstObiegu): PrawaObiegu {
  // Rola liczy się tylko przy aktywnym członkostwie — ten sam predykat co
  // `is_org_writer`. Super-admina rozstrzyga flaga, niezależnie od członkostwa
  // (`is_super_admin()` w polityce też o nie nie pyta).
  const rola = ctx.statusCzlonkostwa === 'active' ? (ctx.rola ?? '') : '';
  return {
    podglad: ctx.superAdmin || (ROLE_PODGLADU_OBIEGU as readonly string[]).includes(rola),
    decyduje: ctx.superAdmin || (ROLE_OBIEGU_PANELU as readonly string[]).includes(rola),
    przepina: ctx.superAdmin,
  };
}

// ── Stany kursora ────────────────────────────────────────────────────────────

/**
 * Stany kursora — `STANY_KURSORA` z receptor-core i CHECK
 * `receptor_kursory_stan_chk` (786). Bramka zestawia wszystkie trzy. Ten sam
 * obiekt, co słownik wspólny (`STANY_KURSORA_RECEPTORA`).
 */
export const STANY_KURSORA_PANELU = STANY_KURSORA_RECEPTORA;

export type StanObiegu = StanKursoraReceptora;

/**
 * Jak obieg wychodzi ze stanu (tabela §13.6.5) — `dziala`, `wznow`, `automat`,
 * `przepiecie`; opis każdego sposobu przy typie w słowniku wspólnym.
 */
export type SposobWyjscia = SposobWyjsciaZeStanu;

export {
  WYJSCIE_ZE_STANU,
  ZDANIA_STANOW,
  ZDANIE_AUTOMATYCZNA_WERYFIKACJA,
  ZDANIE_WYMAGA_PRZEPIECIA,
};

const jestStanem = (stan: string): stan is StanObiegu =>
  (STANY_KURSORA_PANELU as readonly string[]).includes(stan);

/**
 * Zdanie stanu. Stan spoza listy (CHECK rozszerzony migracją nowszą niż ten
 * panel) jest stanem NAZWANYM i zatrzymanym — nigdy zielonym.
 */
export function zdanieStanu(stan: string): string {
  return jestStanem(stan)
    ? ZDANIA_STANOW[stan]
    : `Obieg jest w stanie „${stan}”, którego ta wersja panelu nie zna — odczyt traktuj jako zatrzymany i skontaktuj się z opiekunem.`;
}

export function sposobWyjscia(stan: string): SposobWyjscia {
  return jestStanem(stan) ? WYJSCIE_ZE_STANU[stan] : 'przepiecie';
}

// ── Powody, dla których harmonogram nie zleca ────────────────────────────────

/**
 * Powody niezlecania — `POWODY_HARMONOGRAMU` z receptor-core i CHECK
 * `receptor_kursory_harmonogram_powod_chk` (786). Ten sam obiekt, co słownik
 * wspólny (`POWODY_HARMONOGRAMU_RECEPTORA`); zdania w `ZDANIA_POWODOW_HARMONOGRAMU`.
 */
export const POWODY_HARMONOGRAMU_PANELU = POWODY_HARMONOGRAMU_RECEPTORA;

export type PowodNiezlecania = PowodHarmonogramuReceptora;

export { ZDANIA_POWODOW_HARMONOGRAMU };

/**
 * Zdanie powodu albo `null` (NULL w bazie = harmonogram zleca, nie ma czego
 * mówić). Kod spoza listy dostaje zdanie nazwane — cisza udawałaby „zleca”.
 */
export function zdaniePowoduNiezlecania(powod: string | null | undefined): string | null {
  if (powod === null || powod === undefined || powod === '') return null;
  return (POWODY_HARMONOGRAMU_PANELU as readonly string[]).includes(powod)
    ? ZDANIA_POWODOW_HARMONOGRAMU[powod as PowodNiezlecania]
    : `Odczyt nie jest zlecany z powodu „${powod}”, którego ta wersja panelu nie zna — skontaktuj się z opiekunem.`;
}

// ── Kursor i zakład ──────────────────────────────────────────────────────────

/**
 * Wiersz `receptor_kursory` w kształcie, jaki czyta klient: WYŁĄCZNIE kolumny z
 * grantu migracji 786. `paczka_potwierdzen` i `aktywowal` nie mają grantu
 * dla `authenticated` — `select('*')` kończy się 42501 (patrz
 * `KOLUMNY_KURSORA_OBIEGU` w `receptorCoreApi.ts`).
 */
export interface KursorObiegu {
  id: string;
  organization_id: string;
  zaklad_id: string;
  strumien: string;
  /** `null` = agent prowadzący usunięty (FK `ON DELETE SET NULL (agent_id)`). */
  agent_id: string | null;
  instalacja: string | null;
  epoka: number | null;
  po_seq: number;
  od: string;
  karencja_min: number;
  limit_strony: number;
  limit_czasu_s: number;
  stan: string;
  stan_powod: string | null;
  stan_at: string | null;
  harmonogram_powod: string | null;
  harmonogram_powod_at: string | null;
  porazki_z_rzedu: number;
  ostatnia_porazka_kod: string | null;
  ostatnia_porazka_at: string | null;
  ostatni_sukces_at: string | null;
  aktywowano_at: string;
  ostatni_odczyt_at: string | null;
  ostatni_odczyt: Record<string, unknown> | null;
  liczniki: Record<string, unknown> | null;
  jest_wiecej: boolean;
  updated_at: string;
}

/**
 * Kolumny, o które panel prosi `receptor_kursory` — NAZWANE, nigdy `*`.
 * Rola `authenticated` ma w 786 `GRANT SELECT` na kolumny, nie na tabelę, a
 * `paczka_potwierdzen` i `aktywowal` go nie mają: `select('*')` albo choćby
 * jedna kolumna spoza grantu kończy się całym zapytaniem z 42501. Atrapa tego
 * nie widzi, więc listę zestawia z migracją
 * `tests/unit/apps/kontraktObieguDokumentow.test.ts`.
 */
export const KOLUMNY_KURSORA_OBIEGU = [
  'id',
  'organization_id',
  'zaklad_id',
  'strumien',
  'agent_id',
  'instalacja',
  'epoka',
  'po_seq',
  'od',
  'karencja_min',
  'limit_strony',
  'limit_czasu_s',
  'stan',
  'stan_powod',
  'stan_at',
  'harmonogram_powod',
  'harmonogram_powod_at',
  'porazki_z_rzedu',
  'ostatnia_porazka_kod',
  'ostatnia_porazka_at',
  'ostatni_sukces_at',
  'aktywowano_at',
  'ostatni_odczyt_at',
  'ostatni_odczyt',
  'liczniki',
  'jest_wiecej',
  'updated_at',
] as const;

/**
 * Kolumny `commerce_documents`, których dotyka zapytanie o zaległą kontrolę
 * (w `select` i w filtrach — filtr po kolumnie bez grantu też kończy się 42501).
 * Wszystkie mają grant dla `authenticated` w 786; kontraktu pilnuje ta sama bramka.
 */
export const KOLUMNY_ZALEGLOSCI_KONTROLI = [
  'id',
  'organization_id',
  'stan_zrodla',
  'wystawca_potwierdzenie',
  'wystawca_potwierdzenie_at',
  'wystawca_sprawdzono_at',
] as const;

/** Zakład obiegu = wiersz `locations` organizacji. */
export interface ZakladObiegu {
  id: string;
  name: string;
  /** NULL → odmowa włączenia `zaklad_bez_strefy` (data operacji liczy się w strefie zakładu). */
  timezone: string | null;
}

/**
 * Lokalizacja organizacji ze statusem — nadzbiór {@link ZakladObiegu}. Czyta ją
 * `zakladyOrganizacji` BEZ filtra statusu: nazwy zakładów już związanych
 * z kursorami (karta agenta) i z dokumentami (lista „Obieg dokumentów”, UD-12a).
 * Zakład nieaktywny (usunięty na ekranie Lokalizacje, most 791) zachowuje nazwę.
 */
export interface ZakladOrganizacji extends ZakladObiegu {
  /** `'active'` = aktywna; `'inactive'` i NULL = nieaktywna (`czyLokalizacjaAktywna`). */
  status: string | null;
}

/**
 * Nazwy zakładów KURSORÓW z kluczem zbioru `zaklad_id`, dla którego je wczytano
 * (posortowane, unikalne, po przecinku). Klucz odróżnia „zakładu nie ma na liście”
 * od „lista jest wczytana dla innego zbioru” — np. tuż po „Włącz”, zanim tik
 * zegara doczyta nazwę nowego kursora. `null` = jeszcze nie wczytano, `'blad'` =
 * odczyt się nie udał.
 */
export type NazwyZakladow = { klucz: string; lista: ZakladOrganizacji[] } | 'blad' | null;

/** Klucz zbioru id zakładów (kolejność i powtórzenia bez znaczenia). */
export const kluczZakladow = (ids: readonly string[]): string => [...new Set(ids)].sort().join(',');

/** Czy nazwy wczytano dla zbioru obejmującego `id`. */
function nazwyObejmuja(nazwy: NazwyZakladow, id: string): boolean {
  return nazwy !== null && nazwy !== 'blad' && nazwy.klucz.split(',').includes(id);
}

/**
 * Stan po nieudanym odczycie nazw: poprzednia lista zostaje TYLKO wtedy, gdy była
 * wczytana dla zbioru obejmującego wszystkie bieżące id. Reguła „tablica wygrywa
 * z błędem” trzymałaby po „Włącz” listę sprzed kursora i dawała fałszywe „spoza listy”.
 */
export function nazwyPoBledzie(poprzednie: NazwyZakladow, ids: readonly string[]): NazwyZakladow {
  return poprzednie !== null &&
    poprzednie !== 'blad' &&
    ids.every((id) => nazwyObejmuja(poprzednie, id))
    ? poprzednie
    : 'blad';
}

/**
 * Status zakładu kursora: `aktywny` wyłącznie dla `status = 'active'`, NULL
 * i `'inactive'` → `nieaktywny`. Wczytywanie, błąd, brak na liście i lista
 * wczytana dla innego zbioru id → `nieznany` (o statusie ekran wtedy milczy).
 */
export function stanZakladuKursora(
  nazwy: NazwyZakladow,
  id: string
): 'aktywny' | 'nieaktywny' | 'nieznany' {
  if (nazwy === null || nazwy === 'blad' || !nazwyObejmuja(nazwy, id)) return 'nieznany';
  const z = nazwy.lista.find((x) => x.id === id);
  if (!z) return 'nieznany';
  return czyLokalizacjaAktywna(z.status) ? 'aktywny' : 'nieaktywny';
}

/**
 * Nazwa zakładu KURSORA — z dwóch list, bo lista do włączenia (`listaZakladow`)
 * ma wyłącznie aktywne. Kolejność: nazwy kursorów, potem lista do włączenia (świeżo
 * włączony zakład jest aktywny, więc tam jest), potem stan odczytu nazw.
 * „Spoza listy” wyłącznie wtedy, gdy nazwy wczytano DLA TEGO id, a zakładu w nich
 * nie ma — to prawdziwy trzeci stan (lokalizacja spoza organizacji), nie wyścig.
 */
export function nazwaZakladuKursora(
  nazwy: NazwyZakladow,
  zaklady: readonly ZakladObiegu[] | 'blad' | null | undefined,
  id: string
): string {
  const zNazw =
    nazwy !== null && nazwy !== 'blad' ? nazwy.lista.find((z) => z.id === id) : undefined;
  if (zNazw) return zNazw.name;
  const zListy = Array.isArray(zaklady) ? zaklady.find((z) => z.id === id) : undefined;
  if (zListy) return zListy.name;
  if (nazwy === null || (nazwy !== 'blad' && !nazwyObejmuja(nazwy, id))) {
    return 'zakład (wczytywanie nazwy…)';
  }
  if (nazwy === 'blad') return 'zakład (nazwy nie udało się wczytać)';
  return `zakład spoza listy (id ${id.slice(0, 8)})`;
}

/**
 * Etykieta pozycji zakładu przejmowanego kursora, gdy nie ma go na liście
 * aktywnych (okno „Włącz” w przejęciu). Bez niej pole pokazałoby „Wybierz zakład”,
 * choć zakład jest przesądzony.
 */
export function etykietaZakladuPrzejecia(nazwy: NazwyZakladow, id: string): string {
  if (nazwy === 'blad') return 'zakład obiegu (nazwy nie udało się wczytać)';
  if (nazwy === null || !nazwyObejmuja(nazwy, id)) return 'zakład obiegu (wczytywanie nazwy…)';
  const nazwa = nazwaZakladuKursora(nazwy, null, id);
  return stanZakladuKursora(nazwy, id) === 'nieaktywny' ? `${nazwa} — zakład nieaktywny` : nazwa;
}

/**
 * Nazwa zakładu do wiersza obiegu. Trzy stany, bo każdy mówi co innego:
 * lista jeszcze się wczytuje, lista się nie wczytała, a lista jest i nie ma w niej
 * tego zakładu (spoza organizacji — most 791 lokalizacji nie kasuje, usunięcie
 * zakładu daje `status = 'inactive'`). Skrócony identyfikator
 * podstawiony „na nazwę” brzmiałby jak dane — dokładnie tak powstało
 * „Organization 1250a623” w podsumowaniu dobowym — więc identyfikator występuje
 * wyłącznie w stanie, który nazywa się „spoza listy”.
 */
export function nazwaZakladu(
  zaklady: readonly ZakladObiegu[] | 'blad' | null | undefined,
  zakladId: string
): string {
  if (zaklady === null || zaklady === undefined) return 'zakład (wczytywanie nazwy…)';
  if (zaklady === 'blad') return 'zakład (nazwy nie udało się wczytać)';
  return (
    zaklady.find((z) => z.id === zakladId)?.name ??
    `zakład spoza listy (id ${zakladId.slice(0, 8)})`
  );
}

// ── Wariant wiersza ──────────────────────────────────────────────────────────

export type WariantObiegu =
  /** Rola spoza owner/admin/manager/super-admin — panel nie pyta bazy. */
  | { wariant: 'brak_prawa' }
  /**
   * Brak kursora organizacji — albo kursor istnieje, ale nie ma żywego
   * prowadzącego (`przejecie`): włączenie przy tym agencie przeniesie go tutaj.
   */
  | { wariant: 'nieaktywny'; przejecie: KursorObiegu | null }
  /** Kursor prowadzony przez TEGO agenta, stan `aktywny`. */
  | { wariant: 'aktywny'; kursor: KursorObiegu }
  /** Kursor prowadzony przez TEGO agenta, stan inny niż `aktywny`. */
  | { wariant: 'zatrzymany'; kursor: KursorObiegu }
  /** Kursor organizacji prowadzi INNY, żywy agent. */
  | { wariant: 'inny_agent'; kursor: KursorObiegu };

export interface WejscieWariantu {
  prawa: PrawaObiegu;
  kursory: readonly KursorObiegu[];
  agentId: string;
  /** Agenci organizacji (z odwołanymi): żywy = istnieje i nie jest `revoked`, jak w funkcji włączenia. */
  agenci: ReadonlyArray<{ id: string; status: string }>;
}

export function wariantObiegu(w: WejscieWariantu): WariantObiegu {
  if (!w.prawa.podglad) return { wariant: 'brak_prawa' };

  const wlasny = w.kursory.find((k) => k.agent_id === w.agentId);
  if (wlasny) {
    return wlasny.stan === 'aktywny'
      ? { wariant: 'aktywny', kursor: wlasny }
      : { wariant: 'zatrzymany', kursor: wlasny };
  }

  // Kursor organizacji: do ARCH PR 1.7 najwyżej jeden, a funkcja włączenia bierze
  // najstarszy (`ORDER BY aktywowano_at`) — API zwraca je w tej samej kolejności.
  const cudzy = w.kursory[0];
  if (!cudzy) return { wariant: 'nieaktywny', przejecie: null };

  const prowadzacy =
    cudzy.agent_id === null ? undefined : w.agenci.find((a) => a.id === cudzy.agent_id);
  return prowadzacy !== undefined && prowadzacy.status !== 'revoked'
    ? { wariant: 'inny_agent', kursor: cudzy }
    : { wariant: 'nieaktywny', przejecie: cudzy };
}

// ── Liczniki, zaległość kontroli ─────────────────────────────────────────────

/**
 * Etykieta licznika z kartoteki stanów dokumentu (pole `licznik`). Kod bez
 * pola dostaje etykietę stanu — też z kartoteki, więc drugiego słowa o tym
 * samym stanie nie ma; że cztery kody pole mają, pilnuje test logiki.
 */
const etykietaLicznika = (kod: KodStanuDokumentu): string =>
  STANY_DOKUMENTU[kod].licznik ?? STANY_DOKUMENTU[kod].etykieta;

/**
 * Liczniki, które ekran pokazuje, w tej kolejności. Reszta kluczy `liczniki`
 * (`bez_zmian`, `firma_nieustalona`, `poza_firma`, `przewiniecia`) jest dla
 * diagnostyki, nie dla właściciela firmy. Etykiety stanów dokumentu biorą się
 * z kartoteki — lista „Dokumenty” nazywa ten sam dokument „zmienionym po
 * przekazaniu”, więc karta nie może mówić „zmienione u wystawcy”.
 */
export const LICZNIKI_OBIEGU: ReadonlyArray<{ klucz: string; etykieta: string }> = [
  { klucz: 'zarejestrowane', etykieta: 'zarejestrowane' },
  { klucz: 'potwierdzone', etykieta: etykietaLicznika('potwierdzony') },
  { klucz: 'zmienione', etykieta: etykietaLicznika('zmieniony_w_zrodle') },
  { klucz: 'usuniete_w_zrodle', etykieta: etykietaLicznika('usuniety_w_zrodle') },
  { klucz: 'odmowy', etykieta: etykietaLicznika('odmowa') },
];

/**
 * „zarejestrowane 120 · potwierdzone w Receptorze 118 · …” — TYLKO liczniki,
 * które istnieją. Licznik nieobecny to „jeszcze nie liczono”, a nie zero, więc
 * go nie wymyślamy; zero zapisane w bazie jest informacją i zostaje.
 */
export function opisLicznikow(liczniki: unknown): string | null {
  if (typeof liczniki !== 'object' || liczniki === null || Array.isArray(liczniki)) return null;
  const r = liczniki as Record<string, unknown>;
  const czesci = LICZNIKI_OBIEGU.filter(
    ({ klucz }) => typeof r[klucz] === 'number' && Number.isFinite(r[klucz])
  ).map(({ klucz, etykieta }) => `${etykieta} ${r[klucz] as number}`);
  return czesci.length > 0 ? czesci.join(' · ') : null;
}

/**
 * „Porażki odczytu z rzędu: 3 · ostatnia porażka: przekroczony_czas (12 min
 * temu)” — JEDNO zdanie dla karty agenta i dla paska listy dokumentów. Zero
 * zostaje w zdaniu: na karcie jest informacją, a lista woła je tylko przy
 * porażkach. Kod porażki idzie dosłownie — ekran nie zgaduje, co znaczy.
 */
export function opisPorazekOdczytu(
  k: Pick<KursorObiegu, 'porazki_z_rzedu' | 'ostatnia_porazka_kod' | 'ostatnia_porazka_at'>,
  teraz: number
): string {
  const kiedy = k.ostatnia_porazka_at
    ? ` (${formatujCzasWzgledny(k.ostatnia_porazka_at, teraz)})`
    : '';
  const ostatnia = k.ostatnia_porazka_kod
    ? ` · ostatnia porażka: ${k.ostatnia_porazka_kod}${kiedy}`
    : '';
  return `Porażki odczytu z rzędu: ${k.porazki_z_rzedu}${ostatnia}`;
}

/** Lista „Organizacja › Obieg dokumentów”, zakładka Dokumenty — odnośnik z karty agenta. */
export const ADRES_LISTY_DOKUMENTOW = '/admin/obieg?tab=dokumenty';

/** Cel kontroli potwierdzonych dokumentów (§13.6.4 p. 7, D53-8): co 6 godzin. */
export const CEL_KONTROLI_H = 6;
/**
 * Panel mówi „nie nadąża” przy dwukrotności celu: dokument sprawdzony dawniej
 * niż 12 h znaczy, że kontrola przepustowo przegrywa, a nie że poczeka na swoją
 * kolej (kontrola bierze 100 dokumentów na okno 5 min).
 */
export const PROG_ZALEGLOSCI_KONTROLI_H = 2 * CEL_KONTROLI_H;
/** Kontrola obejmuje dokumenty potwierdzone w ostatnich 35 dniach. */
export const OKNO_KONTROLI_DNI = 35;

/** Granice zapytania o zaległość — ISO z `Z`, liczone od przekazanej chwili. */
export function granicePytaniaOZaleglosc(teraz: number): {
  potwierdzoneOd: string;
  sprawdzoneDo: string;
} {
  return {
    potwierdzoneOd: new Date(teraz - OKNO_KONTROLI_DNI * 24 * 3_600_000).toISOString(),
    sprawdzoneDo: new Date(teraz - PROG_ZALEGLOSCI_KONTROLI_H * 3_600_000).toISOString(),
  };
}

/** „1 dokument sprawdzony” / „2 dokumenty sprawdzone” / „5 dokumentów sprawdzonych”. */
function dokumentySprawdzone(n: number): string {
  const koncowka = n % 10;
  const dziesiatki = n % 100;
  if (n === 1) return '1 dokument sprawdzony';
  if (koncowka >= 2 && koncowka <= 4 && (dziesiatki < 12 || dziesiatki > 14)) {
    return `${n} dokumenty sprawdzone`;
  }
  return `${n} dokumentów sprawdzonych`;
}

/** Zdanie o zaległej kontroli albo `null` (nic nie zalega). */
export function zdanieZaleglosciKontroli(zalegle: number): string | null {
  if (!(zalegle > 0)) return null;
  return `Kontrola potwierdzeń nie nadąża — ${dokumentySprawdzone(zalegle)} dawniej niż ${PROG_ZALEGLOSCI_KONTROLI_H} h.`;
}

// ── Formularze ───────────────────────────────────────────────────────────────

/** Wartości formularza włączenia, które mają domyślną (§13.6.5). `od` NIE MA — celowo. */
export const NASTAWY_DOMYSLNE_OBIEGU = {
  // 821 (20.10.2026): dokument jedzie od razu po zatwierdzeniu w Receptorze — poprawka po przekazaniu to korekta.
  karencja_min: 0,
  limit_strony: 25,
  limit_czasu_s: 300,
} as const;

/** Ile godzin wstecz wolno ustawić `od` (funkcje włączenia i przepięcia, 786). */
export const OKNO_OD_H = 48;

/**
 * Chwila (ms) → wartość pola `datetime-local` w czasie lokalnym przeglądarki
 * („2026-10-15T08:30”). Tylko do atrybutów `min`/`max` — natywny wybieracz nie
 * pozwala wtedy kliknąć daty spoza okna; rozstrzyga i tak {@link walidujOd}.
 */
export function doPolaDatyLokalnej(chwila: number): string {
  const d = new Date(chwila);
  const dwie = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dwie(d.getMonth() + 1)}-${dwie(d.getDate())}T${dwie(d.getHours())}:${dwie(d.getMinutes())}`;
}

export type WynikWalidacji<T> = { ok: true; wartosc: T } | { ok: false; powod: string };

/**
 * `od` z pola `datetime-local` („2026-10-15T08:30”, czas z przeglądarki) →
 * ISO UTC z `Z`. Granice jak w bazie: nie w przyszłości i nie wcześniej niż 48 h
 * temu. Baza i tak rozstrzyga ostatecznie — to zdanie oszczędza człowiekowi
 * rundę do serwera z odpowiedzią, którą można przewidzieć.
 */
export function walidujOd(wartosc: string, teraz: number): WynikWalidacji<string> {
  if (wartosc.trim() === '') {
    return { ok: false, powod: 'Podaj początek obiegu — datę i godzinę.' };
  }
  const chwila = new Date(wartosc).getTime();
  if (!Number.isFinite(chwila)) {
    return { ok: false, powod: 'Podaj początek obiegu — poprawną datę i godzinę.' };
  }
  if (chwila > teraz) {
    return { ok: false, powod: 'Początek obiegu nie może leżeć w przyszłości.' };
  }
  if (chwila < teraz - OKNO_OD_H * 3_600_000) {
    return {
      ok: false,
      powod: `Początek obiegu nie może być wcześniejszy niż ${OKNO_OD_H} godzin temu.`,
    };
  }
  return { ok: true, wartosc: new Date(chwila).toISOString() };
}

const ZAKRESY_NASTAW = {
  karencja_min: { min: 0, max: 1440, nazwa: 'Karencja', jednostka: 'min' },
  limit_strony: { min: 1, max: 100, nazwa: 'Limit strony', jednostka: 'dokumentów' },
  limit_czasu_s: { min: 30, max: 300, nazwa: 'Limit czasu', jednostka: 's' },
} as const;

export interface NastawyObiegu {
  karencja_min: number;
  limit_strony: number;
  limit_czasu_s: number;
}

/** Trzy liczby z pól tekstowych, w zakresach funkcji włączenia (0–1440, 1–100, 30–300). */
export function walidujNastawy(wejscie: {
  karencja_min: string;
  limit_strony: string;
  limit_czasu_s: string;
}): WynikWalidacji<NastawyObiegu> {
  const wynik = {} as NastawyObiegu;
  for (const klucz of Object.keys(ZAKRESY_NASTAW) as Array<keyof NastawyObiegu>) {
    const z = ZAKRESY_NASTAW[klucz];
    const tekst = wejscie[klucz].trim();
    const liczba = Number(tekst);
    if (tekst === '' || !Number.isInteger(liczba) || liczba < z.min || liczba > z.max) {
      return {
        ok: false,
        powod: `${z.nazwa}: liczba całkowita od ${z.min} do ${z.max} (${z.jednostka}).`,
      };
    }
    wynik[klucz] = liczba;
  }
  return { ok: true, wartosc: wynik };
}

/** Sufit powodu — `char_length(p_powod) <= 500` w funkcjach stanu i przepięcia. */
export const MAKS_DLUGOSC_POWODU = 500;

/** Powód wstrzymania, wznowienia i przepięcia: niepusty i do 500 znaków. */
export function walidujPowod(wartosc: string): WynikWalidacji<string> {
  const powod = wartosc.trim();
  if (powod === '') return { ok: false, powod: 'Podaj powód — zostaje w śladzie obiegu.' };
  if (powod.length > MAKS_DLUGOSC_POWODU) {
    return { ok: false, powod: `Powód może mieć najwyżej ${MAKS_DLUGOSC_POWODU} znaków.` };
  }
  return { ok: true, wartosc: powod };
}
